import React from 'react';
import { Shield, Sparkles, ArrowLeft, Mail, Trash2, Info } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';

export function DataDeletion() {
  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-indigo-500/30 selection:text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#030303]/80 backdrop-blur-md z-[100] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-indigo-600 p-1.5 rounded-lg shadow-lg shadow-indigo-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-black tracking-tighter text-white font-display">PlataPost</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-3 text-[10px] font-black text-gray-500 hover:text-white transition-all px-5 py-2.5 rounded-xl bg-white/5 border border-white/5 uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4 cursor-pointer" />
            Back to Home
          </Link>
        </div>
      </nav>

      <main className="pt-32 pb-24 px-6 md:px-10">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-16"
          >
            {/* Header */}
            <div className="space-y-6 text-center md:text-left">
              <div className="bg-rose-500/10 w-fit p-4 rounded-2xl border border-rose-500/20 mx-auto md:mx-0">
                <Trash2 className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight">Data Deletion <span className="text-indigo-500">Instructions.</span></h1>
              <p className="text-xl text-gray-400 max-w-2xl leading-relaxed font-medium">
                At PlataPost, we value your privacy and provide a transparent process for requesting the removal of your data from our systems.
              </p>
            </div>

            {/* Content Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass-card border border-white/5 p-10 rounded-[3rem] space-y-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[40px] -mr-16 -mt-16" />
                <div className="bg-indigo-500/10 w-fit p-3 rounded-xl border border-indigo-500/10 group-hover:scale-110 transition-transform">
                  <Shield className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-2xl font-black tracking-tight">Facebook Data Deletion</h3>
                <p className="text-gray-400 leading-relaxed font-medium">
                  PlataPost does not store your Facebook personal data permanently. If you would like to revoke access and request deletion of any cached interaction data, follow these steps:
                </p>
                <ol className="text-sm text-gray-400 space-y-4 font-medium list-none">
                  {[
                    "Profile Settings & Privacy > Settings",
                    "Click Apps and Websites",
                    "Search for 'PlataPost'",
                    "Click Remove and confirm prompts"
                  ].map((step, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex-shrink-0 w-5 h-5 rounded bg-indigo-600/20 text-indigo-400 flex items-center justify-center text-[10px] font-black">{idx + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="glass-card border border-white/5 p-10 rounded-[3rem] space-y-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-[40px] -mr-16 -mt-16" />
                <div className="bg-indigo-500/10 w-fit p-3 rounded-xl border border-indigo-500/10 group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-2xl font-black tracking-tight">Account Cleanup</h3>
                <p className="text-gray-400 leading-relaxed font-medium">
                  To request a complete removal of your account, company profiles, content plans, and all associated metadata:
                </p>
                <div className="p-6 bg-white/5 rounded-2xl border border-white/5 space-y-2">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Email Request To</p>
                  <a href="mailto:dewaldjordaan5@gmail.com" className="text-xl font-black text-indigo-400 hover:text-indigo-300 transition-colors">
                    dewaldjordaan5@gmail.com
                  </a>
                </div>
                <div className="flex items-start gap-4 p-6 glass-card rounded-2xl border border-indigo-500/10 bg-indigo-500/5">
                  <Info className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-400 font-bold leading-relaxed tracking-tight">
                    Deletion requests are typically processed within 48-72 business hours. Once deleted, this action is irreversible.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="text-center pt-12 border-t border-white/5">
              <p className="text-[10px] font-black text-gray-600 uppercase tracking-[0.3em]">
                © 2026 PlataPost AI • Data Privacy & Compliance
              </p>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
